import React, {useState, useRef} from "react";
import styled from "styled-components";
import {Label} from "reactstrap";
import {FaBurn, FaExclamationTriangle} from "react-icons/fa";
import {BsFuelPump, BsLightningFill} from "react-icons/bs";
import {ItemIcon} from "./ItemIcon";

const CardButton = styled.button<{ $running: boolean }>`
  background-color: ${props => props.$running ? '#4caf50' : '#9e9e9e'};
  border-radius: 8px;
  border: 4px solid ${props => props.$running ? '#81c784' : '#e0e0e0'};
  width: 90px;
  height: 100px;
  margin-right: 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  cursor: pointer;
  padding: 4px;
  padding-top: 0px;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background-color: ${props => props.$running ? '#66bb6a' : '#757575'};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MachineIcon = styled.div`
  font-size: 20px;
  color: #fff;
  margin-bottom: 2px;
  position: relative;
  z-index: 2;
`;

const ProgressTrack = styled.div`
  position: absolute;
  bottom: 8px;
  left: 14px;
  right: 14px;
  height: 8px;
  border-radius: 4px;
  background: #2f2f2f;
  overflow: hidden;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 2px;
`;

const ProgressFill = styled.div<{ $progress: number; $active: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  width: ${(props) => Math.min(100, props.$progress)}%;
  height: 100%;
  background: ${(props) => (props.$active ? "#ff9800" : "#777")};
  transition: width 0.35s linear, background 0.2s ease-in-out;
`;

const ProgressPercent = styled.span`
  font-size: 7px;
  color: #fff;
  font-weight: bold;
  position: relative;
  z-index: 1;
`;

const ProductionLabel = styled.div<{ $running: boolean }>`
  position: absolute;
  bottom: 6px;
  left: 14px;
  right: 14px;
  font-size: 8px;
  font-weight: bold;
  color: ${props => props.$running ? '#66ff66' : '#999'};
  filter: ${props => props.$running ? 'brightness(1.3)' : 'brightness(1)'};
  text-shadow: ${props => props.$running ? '0 0 2px rgba(102, 255, 102, 0.5)' : 'none'};
  text-align: center;
  z-index: 2;
`;

const StatsRow = styled.div<{ $isElectric?: boolean }>`
  display: flex;
  justify-content: center;
  width: 100%;
  font-size: 9px;
  font-weight: bold;
  margin-top: ${props => props.$isElectric ? '16px' : '0px'};
  z-index: 2;
  position: relative;
  padding: 0 2px;
`;

const PowerBadge = styled.div`
  color: #42a5f5;
  filter: brightness(1.3);
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 9px;
  font-weight: bold;
`;

const WarningIcon = styled.div`
  position: absolute;
  right: 4px;
  top: 0px;
  color: #ffb300;
  font-size: 14px;
`;

const InputWarningIcon = styled.div`
  position: absolute;
  right: 4px;
  top: 18px;
  color: #ff6b35;
  font-size: 14px;
`;

const FuelBarContainer = styled.div<{ $blink: boolean; $disabled?: boolean }>`
  position: absolute;
  left: 4px;
  top: 0px;
  bottom: 46px;
  width: 12px;
  background: #1a1a1a;
  border: 1px solid #444;
  border-radius: 2px;
  overflow: hidden;
  animation: ${(props) => (props.$blink ? "blink-full 0.4s ease-in-out 2" : "none")};
  opacity: ${(props) => (props.$disabled ? 0.3 : 1)};
  filter: ${(props) => (props.$disabled ? "grayscale(100%)" : "none")};

  @keyframes blink-full {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
      filter: brightness(1.5);
    }
  }
`;

const FuelBarFill = styled.div<{ $percent: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: ${(props) => props.$percent}%;
  background: linear-gradient(to top, #ff9800, #ffc107);
  transition: height 0.3s ease;
`;

const FuelIcon = styled.div<{ $show: boolean; $blink: boolean; $isEmpty: boolean; $blinkRed: boolean; $disabled?: boolean }>`
  position: absolute;
  left: 2px;
  bottom: 30px;
  color: #ffb74d;
  filter: brightness(1.3);
  font-size: 14px;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  display: ${(props) => (props.$show ? "block" : "none")};
  animation: ${(props) =>
    props.$blink ? "blink-fuel 0.6s ease-in-out" :
    props.$isEmpty ? "blink-fuel-empty 1s ease-in-out 2" :
    props.$blinkRed ? "blink-fuel-red 0.4s ease-in-out 3" : "none"};
  z-index: 10;
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};
  opacity: ${(props) => (props.$disabled ? 0.3 : 1)};
  filter: ${(props) => (props.$disabled ? "grayscale(100%)" : "brightness(1.3)")};

  @keyframes blink-fuel {
    0%, 100% {
      color: #ffb74d;
      filter: brightness(1.3);
    }
    25% {
      color: #ffeb3b;
      filter: brightness(2) drop-shadow(0 0 8px #ffeb3b);
    }
    50% {
      color: #ffb74d;
      filter: brightness(1.3);
    }
    75% {
      color: #ffeb3b;
      filter: brightness(2) drop-shadow(0 0 8px #ffeb3b);
    }
  }

  @keyframes blink-fuel-empty {
    0%, 100% {
      color: #c78a3d;
      filter: brightness(1.0);
    }
    50% {
      color: #ffeb3b;
      filter: brightness(3.5) drop-shadow(0 0 12px #ffeb3b) drop-shadow(0 0 20px rgba(255, 235, 59, 0.6));
    }
  }

  @keyframes blink-fuel-red {
    0%, 100% {
      color: #ffb74d;
      filter: brightness(1.3);
    }
    50% {
      color: #ff3333;
      filter: brightness(2) drop-shadow(0 0 8px #ff3333);
    }
  }

  &:hover {
    color: #ffc107;
    filter: brightness(1.5);
  }
  &:active {
    transform: scale(0.9);
  }
`;

const FloatingText = styled.div<{ $visible: boolean; $index: number }>`
  position: absolute;
  left: 10px;
  top: 27px;
  transform: translateX(-50%);
  color: #4caf50;
  font-size: 14px;
  font-weight: bold;
  pointer-events: none;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  animation: ${(props) => (props.$visible ? `float-up 1s ease-out` : "none")};
  animation-delay: ${(props) => props.$index * 0.05}s;

  @keyframes float-up {
    0% {
      transform: translateX(-50%) translateY(0);
      opacity: 1;
    }
    100% {
      transform: translateX(-50%) translateY(-30px);
      opacity: 0;
    }
  }
`;

const FuelBarBlink = styled.div`
  @keyframes blink-full {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
      filter: brightness(1.5);
    }
  }
`;

const RemoveButton = styled.div`
  position: absolute;
  right: -4px;
  top: -4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #f44336;
  color: white;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
  pointer-events: auto;
  ${CardButton}:hover & {
    opacity: 1;
  }
  &:hover {
    background: #d32f2f;
    transform: scale(1.1);
  }
  &:active {
    transform: scale(0.9);
  }
`;

const LabelStyled = styled(Label)`
  position: relative;
  z-index: 2;
`;

export type MachineCardProps = {
  label: string;
  running: boolean;
  progressMs: number;
  cycleTimeMs: number;
  onToggle: () => void;
  showFuelWarning?: boolean;
  showInputWarning?: boolean; // Show warning when no input resources available
  inputStarved?: boolean; // True when machine has no input resources
  fuelBuffer?: number; // 0-100
  maxFuelBuffer?: number; // default 100
  onAddFuel?: () => void;
  showFuelIcon?: boolean; // Show manual fueling icon
  autoFuelingEnabled?: boolean; // Whether auto-fueling tech is unlocked
  fuelAmountPerClick?: number; // Amount of fuel being added per click
  onRemove?: () => void; // Callback to remove this machine
  minFuelForOneCycle?: number; // Minimum fuel needed for one cycle (in fuel units)
  machineItemId?: string; // Item ID to show machine icon from ItemIcon
  productionRate?: number; // Production rate in items per second
  powerUse?: number; // Power consumption in kW (for electric machines)
  dataTestId?: string; // Custom test ID for testing purposes
  fuelAvailable?: number; // Amount of fuel available in inventory
  onNoFuelClick?: () => void; // Callback when user clicks fuel button without fuel
};

type FloatingTextInstance = {
  id: number;
  amount: number;
  visible: boolean;
};

export function MachineCard({
  label,
  running,
  progressMs,
  cycleTimeMs,
  onToggle,
  showFuelWarning = false,
  showInputWarning = false,
  inputStarved = false,
  fuelBuffer,
  maxFuelBuffer = 100,
  onAddFuel,
  showFuelIcon = false,
  autoFuelingEnabled = false,
  fuelAmountPerClick = 1,
  onRemove,
  minFuelForOneCycle = 0.01, // Default to very small amount to handle rounding errors
  machineItemId,
  productionRate,
  powerUse,
  dataTestId,
  fuelAvailable = 0,
  onNoFuelClick,
}: MachineCardProps) {
  const progressPercent = running && cycleTimeMs ? (progressMs / cycleTimeMs) * 100 : 0;
  const [floatingTexts, setFloatingTexts] = useState<FloatingTextInstance[]>([]);
  const [blinkFuelIcon, setBlinkFuelIcon] = useState(false);
  const [blinkFuelGauge, setBlinkFuelGauge] = useState(false);
  const [blinkFuelRed, setBlinkFuelRed] = useState(false);
  const nextIdRef = useRef(0);

  const hasFuelBuffer = fuelBuffer !== undefined;
  const fuelPercent = hasFuelBuffer ? (fuelBuffer / maxFuelBuffer) * 100 : 0;
  const isFuelFull = hasFuelBuffer && fuelBuffer >= maxFuelBuffer;

  // Show fuel icon if manual fueling is available (not auto-fueling or explicitly shown)
  const shouldShowFuelIcon = showFuelIcon || (hasFuelBuffer && !autoFuelingEnabled && onAddFuel);

  const handleAddFuel = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Prevent fueling when auto-fueling is enabled
    if (autoFuelingEnabled) {
      return;
    }

    if (onAddFuel) {
      // Check if fuel is available in inventory
      if (fuelAvailable < fuelAmountPerClick) {
        // Blink the fuel icon red to indicate no fuel available
        setBlinkFuelRed(true);
        setTimeout(() => setBlinkFuelRed(false), 1200); // 3 blinks at 400ms each

        // Trigger callback to blink the manual mining button
        if (onNoFuelClick) {
          onNoFuelClick();
        }
        return;
      }

      // Check if fuel is already full
      if (isFuelFull) {
        // Blink the fuel gauge to indicate it's full
        setBlinkFuelGauge(true);
        setTimeout(() => setBlinkFuelGauge(false), 800); // 2 blinks at 400ms each
        return;
      }

      onAddFuel();

      // Add new floating text instance
      const newId = nextIdRef.current++;
      const newText: FloatingTextInstance = {
        id: newId,
        amount: fuelAmountPerClick,
        visible: true,
      };

      setFloatingTexts(prev => [...prev, newText]);

      // Remove after animation completes
      setTimeout(() => {
        setFloatingTexts(prev => prev.filter(text => text.id !== newId));
      }, 1000);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove();
    }
  };

  const handleToggle = () => {
    // Only check fuel for machines that use fuel (burner machines)
    // Electric machines don't have fuelBuffer and can always be toggled
    // If auto-fueling is enabled, skip fuel check (system will auto-add fuel)
    if (!autoFuelingEnabled && !running && fuelBuffer !== undefined && fuelBuffer < minFuelForOneCycle) {
      // Trigger blink animation - don't allow toggle
      setBlinkFuelIcon(true);
      setTimeout(() => setBlinkFuelIcon(false), 600); // Match animation duration
      return; // Prevent toggle if insufficient fuel
    }

    onToggle();
  };

  return (
    <CardButton
      type="button"
      aria-pressed={running}
      onClick={handleToggle}
      data-testid={dataTestId || `${label.toLowerCase()}-card`}
      $running={running}
    >
      {machineItemId && (
        <MachineIcon>
          <ItemIcon
            itemId={machineItemId}
            size={48}
            background="transparent"
          />
        </MachineIcon>
      )}
      <LabelStyled className="mb-0" style={{fontSize: "10px", color: "#fff", fontWeight: "bold", marginTop: "0px"}}>
        {label}
      </LabelStyled>

      <StatsRow $isElectric={powerUse !== undefined}>
        {powerUse !== undefined && (
          <PowerBadge title={`Power consumption: ${powerUse} kW`}>
            <BsLightningFill /> {powerUse}kW
          </PowerBadge>
        )}
      </StatsRow>

      <ProgressTrack aria-label={`${label} progress`}>
        <ProgressFill $progress={progressPercent} $active={running} />
        {running && <ProgressPercent>{Math.floor(progressPercent)}%</ProgressPercent>}
      </ProgressTrack>

      {productionRate !== undefined && (
        <ProductionLabel $running={running}>
          {productionRate.toFixed(2)}/s
        </ProductionLabel>
      )}

      {showFuelWarning && (
        <WarningIcon title="No fuel available">
          <FaBurn />
        </WarningIcon>
      )}

      {showInputWarning && (
        <InputWarningIcon title="No input resources available">
          <FaExclamationTriangle />
        </InputWarningIcon>
      )}

      {hasFuelBuffer && (
        <FuelBarContainer
          title={autoFuelingEnabled ? "Auto-fueling enabled" : `Fuel: ${fuelBuffer.toFixed(1)}/${maxFuelBuffer}`}
          $blink={blinkFuelGauge}
          $disabled={autoFuelingEnabled}
        >
          <FuelBarFill $percent={fuelPercent} />
        </FuelBarContainer>
      )}

      {shouldShowFuelIcon && (
        <FuelIcon
          $show={true}
          $blink={blinkFuelIcon}
          $isEmpty={fuelBuffer !== undefined && fuelBuffer < minFuelForOneCycle}
          $blinkRed={blinkFuelRed}
          $disabled={autoFuelingEnabled}
          onClick={handleAddFuel}
          title={autoFuelingEnabled ? "Auto-fueling enabled" : isFuelFull ? "Fuel tank is full!" : fuelAvailable < fuelAmountPerClick ? "No fuel available!" : "Click to add fuel"}
        >
          <BsFuelPump />
        </FuelIcon>
      )}

      {floatingTexts.map((text, index) => (
        <FloatingText
          key={text.id}
          $visible={text.visible}
          $index={index}
        >
          +{text.amount}
        </FloatingText>
      ))}

      {onRemove && (
        <RemoveButton onClick={handleRemove} title="Remove machine">
          ×
        </RemoveButton>
      )}
    </CardButton>
  );
}
